import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React__default, { useState, useRef, useEffect } from 'react';
import ReactDom from 'react-dom';
import useActiveElement from '../hooks/useActiveElement.js';
import DisableBackgroundScroll from '../utils/DisableBackgroundScroll.js';
import FocusTrap from '../utils/FocusTrap.js';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import useCanvasId from '../utils/useCanvasId.js';
import Button from './Button.js';
import Grid from './Grid.js';
import Heading from './Heading.js';
import Overlay from './Overlay.js';

var _excluded = ["as", "id", "className", "bodyText", "headingText", "onApprove", "onRefuse", "isVisible", "refuseButtonText", "approveButtonText", "ariaLabelId", "parentElementId", "scrollBehavior"];
var Dialog = function Dialog(_ref) {
  var _ref$as = _ref.as,
    As = _ref$as === void 0 ? 'div' : _ref$as,
    id = _ref.id,
    className = _ref.className,
    bodyText = _ref.bodyText,
    headingText = _ref.headingText,
    onApprove = _ref.onApprove,
    onRefuse = _ref.onRefuse,
    isVisible = _ref.isVisible,
    refuseButtonText = _ref.refuseButtonText,
    approveButtonText = _ref.approveButtonText,
    ariaLabelId = _ref.ariaLabelId,
    _ref$parentElementId = _ref.parentElementId,
    parentElementId = _ref$parentElementId === void 0 ? 'canvasPortalContainer' : _ref$parentElementId,
    _ref$scrollBehavior = _ref.scrollBehavior,
    scrollBehavior = _ref$scrollBehavior === void 0 ? 'default' : _ref$scrollBehavior,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  alertUserOfDeprecatedProps('Dialog', otherProps);
  var ariaLabelCanvasId = useCanvasId(id, 'dialog');

  // Stores a DOM reference to the parent that we should attach this modal to
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    portalRef = _useState2[0],
    setPortalRef = _useState2[1];
  // Stores a reference to this component. In the event we can't find the parentElementId to attach the modal to,
  // we instead use this reference to attach to the parent of this component instead.
  var thisComponent = useRef(null);
  useEffect(function () {
    // We need to ensure that the parent has rendered before attempting to get a reference to it.
    // useEffect ensures that has occured.
    if (thisComponent.current) {
      var el = document.getElementById(parentElementId) || thisComponent.current.parentElement; // If parentElementId is not found, default to the parent of this component
      setPortalRef(el);
    }
  }, [thisComponent, parentElementId]);

  // Cache the current focused dom element across re-renders
  var focusedElRef = useActiveElement(isVisible);
  var onRefuseHandler = function onRefuseHandler(event) {
    event.stopPropagation();
    onRefuse(event);
  };
  var finalClasses = classnames('C-Dialog', {
    isVisible: isVisible
  }, className);
  var dialogContainerClass = classnames('C-Dialog__container', _defineProperty({}, "--scroll-".concat(scrollBehavior), scrollBehavior));
  var dialogContentClass = classnames('C-Dialog__content', _defineProperty({}, "--scroll-".concat(scrollBehavior), scrollBehavior));
  var header = /*#__PURE__*/React__default.createElement(Grid.Container, {
    justify: "space-between",
    className: "header"
  }, /*#__PURE__*/React__default.createElement(Grid.Item, {
    xs: true
  }, /*#__PURE__*/React__default.createElement(Heading, {
    size: 2,
    className: "hc-fs-xl",
    tabIndex: "-1",
    id: ariaLabelId ? ariaLabelId : ariaLabelCanvasId
  }, headingText)));
  var content;
  if (bodyText) {
    content = /*#__PURE__*/React__default.createElement(As, {
      className: dialogContentClass
    }, /*#__PURE__*/React__default.createElement("p", null, bodyText));
  }
  var footer;
  if (approveButtonText || refuseButtonText) {
    footer = /*#__PURE__*/React__default.createElement(Grid.Container, {
      spacing: "dense",
      className: "hc-mt-expanded",
      direction: "row-reverse"
    }, approveButtonText && /*#__PURE__*/React__default.createElement(Grid.Item, null, /*#__PURE__*/React__default.createElement(Button, {
      onClick: onApprove,
      type: "primary"
    }, approveButtonText)), refuseButtonText && /*#__PURE__*/React__default.createElement(Grid.Item, null, /*#__PURE__*/React__default.createElement(Button, {
      type: "secondary",
      onClick: onRefuseHandler
    }, refuseButtonText)));
  }
  if (!portalRef) {
    return /*#__PURE__*/React__default.createElement("div", {
      ref: thisComponent
    });
  }
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ReactDom.createPortal(/*#__PURE__*/React__default.createElement("div", {
    className: finalClasses,
    "aria-modal": true
  }, /*#__PURE__*/React__default.createElement(DisableBackgroundScroll, {
    isActive: isVisible
  }), /*#__PURE__*/React__default.createElement(FocusTrap, _extends({
    active: isVisible,
    returnFocusTo: focusedElRef,
    onEscape: onRefuseHandler,
    className: dialogContainerClass,
    "aria-labelledby": ariaLabelId ? ariaLabelId : ariaLabelCanvasId
  }, otherProps), header, content, footer), /*#__PURE__*/React__default.createElement(Overlay, {
    type: "subtle",
    onClick: onRefuseHandler,
    isVisible: isVisible
  })), portalRef));
};
Dialog.displayName = 'Dialog';

export { Dialog as default };
